// This Provider is intended to follow the Context/Provider pattern, while also
// using the Redux store, already in use. Typically, Contexts contain their own
// state, but in order to avoid large refactors, we support both patterns.

import React, { createContext, useContext, useDebugValue, useEffect, useCallback, useState } from "react";
import { connect, shallowEqual, useDispatch, useSelector } from "react-redux";
import { ReviewsSort } from "rtr-constants";
import { childrenPropType } from "../propTypes";
import { getDisplayName } from "../source/hoc/utils";
import { requestReviews } from "actions/reviews-actions";
import { useUser } from "./UserContext";

// important for reviews to default to null, instead of, say, {}, so that Pages
// know to use the stale server-side data until data base been fetched.
// unfortunately, hasLoaded has not yet been implemented... yet!
const ReviewsContext = createContext({ reviews: null, hasLoaded: false });
const stateKey = "reviews";

// plucks reviews from the Redux Context
function reviewsSelector(state) {
  const { [stateKey]: reviews, [`${stateKey}HasLoaded`]: hasLoaded, [`${stateKey}Error`]: error } = state;

  return { reviews, error, hasLoaded };
}

/////////////////////////////////
//                             //
//  Hook                       //
//  for functional Components  //
//                             //
/////////////////////////////////

export function useReviews() {
  return useContext(ReviewsContext);
}

/////////////////////////////////
//                             //
//  HOC                        //
//  for class Components       //
//                             //
/////////////////////////////////

export function withReviews() {
  return WrappedComponent => {
    const C = connect(reviewsSelector)(WrappedComponent);

    C.displayName = `withReviews(${getDisplayName(WrappedComponent)})`;

    return C;
  };
}

/**
 * @param {object} props
 * @param {function} props.children a ***function*** that takes userProfile as a
 * parameter. used to render your experimental content
 * @param {function} props.onInitialRender a function called only once, when
 * data fetching is complete
 * @param {function} props.fallback a function which returns the content you
 * wish to display while fetching
 * @description Wrap your experimental content in this component to render once
 * user has been loaded. `children` should be a function
 */
export function ReviewsSuspense({ fallback, onInitialRender, children: fnRenderChildren }) {
  let content;
  const { reviews, hasLoaded, error } = useReviews();

  // call the onInitialRender method only once, when hasLoaded is true
  // useCallback to guard against a case where hasLoaded toggles more than once
  const handleInitialRender = useCallback(() => hasLoaded && onInitialRender?.(reviews), [hasLoaded]);

  // call the memoized function each time hasLoaded changes (useCallback ensures it
  // is only called once per hasLoaded value)
  useEffect(() => {
    handleInitialRender();
  }, [hasLoaded]);

  if (hasLoaded) {
    // happy path
    content = fnRenderChildren?.(reviews, error);
  } else if (fallback) {
    // fallback
    content = fallback();
  }

  return content ?? null;
}

// I'd love to fetch user here, but we have to consider full page caching
async function getInitialReduxState() {
  return Promise.resolve({
    [stateKey]: void 0,
    [`${stateKey}HasLoaded`]: false,
    [`${stateKey}Error`]: null,
  });
}

// unfortunately, the Reviews Component keeps an internal state of WLM form
// values, so we have to guess what they are going to be initially.
// TODO: move wlmFilters to the Redux Store
export function ReviewsProvider({ children }) {
  const [productId, setProductId] = useState();
  const [hasLoaded, setHasLoaded] = useState(false);
  const dispatch = useDispatch();
  const { reviews } = useSelector(reviewsSelector, shallowEqual);
  const { hasLoaded: userProfileHasLoaded } = useUser() || {};

  const handleSuccess = () => setHasLoaded(true);

  useEffect(() => {
    if (!productId) return;
    if (!userProfileHasLoaded) return;

    setHasLoaded(false);

    const params = {
      sort: ReviewsSort.FEATURED,
    };

    dispatch(requestReviews(productId, params, "initial-load", { onSuccess: handleSuccess }));

    // TODO: handle errors!
  }, [productId, userProfileHasLoaded]);

  useDebugValue(hasLoaded ? "Loaded" : "Pending");

  // provide hasLoaded once implemented
  return (
    <ReviewsContext.Provider value={{ reviews, hasLoaded, productId, setProductId }}>
      {children}
    </ReviewsContext.Provider>
  );
}

ReviewsProvider.getInitialReduxState = getInitialReduxState;

ReviewsProvider.propTypes = {
  children: childrenPropType.isRequired,
};

export default {
  ...ReviewsContext,
  Provider: ReviewsProvider,
};
